import React, { useCallback, useEffect, useRef, useState } from "react";
import { Alert, Button, Checkbox, Modal, message } from "antd";
import { connect } from "react-redux";
import { withRouter } from "react-router-dom";
import Api from "../../helpers/apiHandler";
import { formatDisplayDate, isApiOk } from "../../containers/UsdaIngredients/usdaIngredientApi";
import "./UsdaUpdatePrompt.css";

const api = new Api();

/** Re-check at most this often while navigating. */
const CHECK_INTERVAL_MS = 5 * 60 * 1000;
/** "Ask me later" hides the prompt for the rest of the browser session (per user + company; cleared on logout). */
const SNOOZE_KEY = "usdaUpdatePromptSnoozed";

const snoozeKeyFor = (userId, companyId) =>
  userId && companyId ? `${SNOOZE_KEY}:${userId}:${companyId}` : userId ? `${SNOOZE_KEY}:${userId}` : SNOOZE_KEY;

const isSnoozed = (key) => {
  try {
    return sessionStorage.getItem(key) === "1";
  } catch (e) {
    return false;
  }
};

const KNOWN_UNITS = {
  g: "g",
  mg: "mg",
  ug: "µg",
  mcg: "µg",
  µg: "µg",
  μg: "µg",
  kj: "kJ",
  kcal: "kcal",
  cal: "kcal",
  iu: "IU",
};

const unitOf = (value) => {
  const match = String(value || "").trim().match(/^(.*\d)\s+([^\d\s]+)$/);
  if (!match) return "";
  return KNOWN_UNITS[match[2].toLowerCase()] || "";
};

/** "1.84 G" and "25 KCAL" become "1.84 g" and "25 kcal". Names and categories stay as written. */
const formatDisplayValue = (value) => {
  if (value === null || value === undefined || String(value).trim() === "") return "Not set";
  const text = String(value).trim();
  const match = text.match(/^(.*\d)\s+([^\d\s]+)$/);
  if (!match) return text;
  const unit = KNOWN_UNITS[match[2].toLowerCase()];
  return unit ? `${match[1].trim()} ${unit}` : text;
};

/** "Nutrition: Energy" plus a kJ or kcal amount becomes "Energy (kJ)" or "Energy (kcal)". */
const formatFieldLabel = (field, oldValue, newValue) => {
  const raw = String(field || "");
  const name = raw.startsWith("Nutrition: ") ? raw.slice("Nutrition: ".length) : raw;
  const unit = unitOf(newValue) || unitOf(oldValue);
  if (/^energy$/i.test(name) && unit) return `Energy (${unit})`;
  return name;
};

const STALE_MESSAGE = "This ingredient was updated again — please review the latest changes.";
const ALREADY_HANDLED_MESSAGE =
  "This ingredient update was already handled in another session. The latest data has been loaded.";

/**
 * Asks the logged-in company whether to apply Super Admin changes to its USDA ingredients.
 * Nothing changes unless the user chooses "Update". Render only for company users.
 */
const UsdaUpdatePrompt = ({ history, userId, companyId }) => {
  const [updates, setUpdates] = useState([]);
  const [selected, setSelected] = useState([]);
  const [expanded, setExpanded] = useState([]);
  const [visible, setVisible] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [updatedRecipes, setUpdatedRecipes] = useState([]);
  const lastCheckedAt = useRef(0);
  const visibleRef = useRef(false);
  const snoozeKey = snoozeKeyFor(userId, companyId);

  const recipesRef = useRef(0);

  useEffect(() => {
    visibleRef.current = visible;
  }, [visible]);

  useEffect(() => {
    recipesRef.current = updatedRecipes.length;
  }, [updatedRecipes]);

  /** reload = refresh the list while the modal is already open (after a stale revision). */
  const check = useCallback(
    (force, reload) => {
      if (!reload && (visibleRef.current || isSnoozed(snoozeKey))) {
        return;
      }
      if (!force && Date.now() - lastCheckedAt.current < CHECK_INTERVAL_MS) {
        return;
      }
      lastCheckedAt.current = Date.now();
      api.get("ingredient/usda-updates").then((res) => {
        const list = isApiOk(res) && Array.isArray(res.data) ? res.data : [];
        if (!list.length) {
          if (reload) {
            setUpdates([]);
            setSelected([]);
            if (!recipesRef.current) {
              setVisible(false);
            }
          }
          return;
        }
        setUpdates(list);
        setSelected(list.map((item) => item.ingredientId));
        setExpanded(list.length === 1 ? [list[0].ingredientId] : []);
        if (!reload) {
          setUpdatedRecipes([]);
        }
        setVisible(true);
      });
    },
    [snoozeKey]
  );

  useEffect(() => {
    if (!visible) return;
    document.querySelectorAll(".ant-tooltip").forEach((node) => {
      if (node.parentNode) node.parentNode.removeChild(node);
    });
  }, [visible]);

  useEffect(() => {
    check(true);
    const unlisten = history && history.listen ? history.listen(() => check(false)) : null;
    return () => {
      if (unlisten) {
        unlisten();
      }
    };
  }, [check, history]);

  const send = (endpoint, accepting) => {
    if (!selected.length || processing) {
      return;
    }
    const revisions = {};
    updates.forEach((item) => {
      if (selected.includes(item.ingredientId)) {
        revisions[item.ingredientId] = item.revision;
      }
    });
    setProcessing(true);
    api
      .post(endpoint, { isMultipart: false, data: { ingredientIds: selected, revisions } })
      .then((res) => {
        if (!isApiOk(res)) {
          message.error((res && res.message) || "Could not save your choice. Please try again.");
          return;
        }
        const skipped = res.data && Array.isArray(res.data.skipped) ? res.data.skipped : [];
        const skippedIds = new Set(skipped.map((skip) => Number(skip && skip.ingredientId)));
        const appliedIds = selected.filter((id) => !skippedIds.has(Number(id)));
        const nameOf = (ingredientId) => {
          const match = updates.find((item) => Number(item.ingredientId) === Number(ingredientId));
          return match ? match.ingredientName : "Ingredient";
        };
        skipped
          .filter((skip) => skip && skip.reason === "name_conflict")
          .forEach((skip) =>
            message.warning(
              `${nameOf(skip.ingredientId)}: the new name is already used by another ingredient, so the name was kept.`
            )
          );
        const alreadyHandled = skipped.some(
          (skip) => skip && (skip.reason === "not_pending" || skip.reason === "not_found")
        );
        const stale = skipped.some((skip) => skip && skip.reason === "stale_revision");
        const recipes =
          accepting && appliedIds.length && res.data && Array.isArray(res.data.recipes) ? res.data.recipes : [];
        const answered = new Set(appliedIds.map((id) => Number(id)));
        skipped.forEach((skip) => {
          if (skip && (skip.reason === "not_pending" || skip.reason === "not_found")) {
            answered.add(Number(skip.ingredientId));
          }
        });
        const remaining = updates.filter((item) => !answered.has(Number(item.ingredientId)));
        setUpdates(remaining);
        setSelected(remaining.map((item) => item.ingredientId));
        if (recipes.length) {
          recipesRef.current = recipes.length;
          setUpdatedRecipes(recipes);
        }
        if (!remaining.length && !recipes.length) {
          setVisible(false);
          if (alreadyHandled) {
            message.info(ALREADY_HANDLED_MESSAGE);
          }
        } else if (stale) {
          message.warning(STALE_MESSAGE);
        }
        if (alreadyHandled || stale) {
          check(true, true);
          return;
        }
        if (recipes.length) {
          return;
        }
        if (!remaining.length) {
          setVisible(false);
        }
      })
      .finally(() => setProcessing(false));
  };

  const askLater = () => {
    try {
      sessionStorage.setItem(snoozeKey, "1");
    } catch (e) {
      // storage unavailable: the prompt simply comes back on the next check
    }
    setVisible(false);
  };

  const continueAfterRecipes = () => {
    setUpdatedRecipes([]);
    if (!updates.length) {
      setVisible(false);
    }
  };

  const toggleSelected = (ingredientId, checked) =>
    setSelected((prev) => (checked ? [...prev, ingredientId] : prev.filter((id) => id !== ingredientId)));

  const toggleExpanded = (ingredientId) =>
    setExpanded((prev) => (prev.includes(ingredientId) ? prev.filter((id) => id !== ingredientId) : [...prev, ingredientId]));

  /** New tab, so the list of recipes to re-save stays visible here. */
  const openRecipe = (recipe) => {
    window.open(`/recipe/edit/${recipe.id}`, "_blank");
    // setVisible(false);
    // history.push(`/recipe/edit/${recipe.id}`);
  };

  const recipesStep = updatedRecipes.length > 0;
  const title = recipesStep ? "Update your recipe labels" : "Ingredient information updated";

  const footer = recipesStep
    ? [
        <Button key="continue" type="primary" onClick={continueAfterRecipes}>
          {updates.length ? "Continue" : "Done"}
        </Button>,
      ]
    : [
        <Button key="later" onClick={askLater} disabled={processing}>
          Ask me later
        </Button>,
        <Button key="keep" onClick={() => send("ingredient/usda-updates/decline", false)} disabled={processing || !selected.length}>
          Keep my data
        </Button>,
        <Button
          key="update"
          type="primary"
          loading={processing}
          disabled={!selected.length}
          onClick={() => send("ingredient/usda-updates/accept", true)}
        >
          Update ({selected.length})
        </Button>,
      ];

  const renderComparedValue = (value, emphasize) => {
    const text = formatDisplayValue(value);
    if (text === "Not set") return <span className="usda-update-empty">Not set</span>;
    if (emphasize) return <span className="usda-update-new">{text}</span>;
    return text;
  };

  return (
    <Modal
      visible={visible}
      title={title}
      width={760}
      zIndex={12000}
      centered
      wrapClassName="usda-update-modal"
      className="generalModal"
      maskClosable={false}
      onCancel={() => (processing ? null : setVisible(false))}
      footer={footer}
      destroyOnClose
    >
      {recipesStep ? (
        <div>
          <p className="usda-update-intro">
            Your ingredients now use the new information. These recipes use them: open each one (it opens in a new
            tab) and save it to update its saved label.
          </p>
          <ul style={{ paddingLeft: 18 }}>
            {updatedRecipes.map((recipe) => (
              <li key={recipe.id}>
                <Button type="link" style={{ padding: 0 }} onClick={() => openRecipe(recipe)}>
                  {recipe.name}
                </Button>
              </li>
            ))}
          </ul>
        </div>
      ) : (
        <div>
          <p className="usda-update-intro">
            The nutritional information for{" "}
            <strong>{updates.length === 1 ? updates[0].ingredientName : `${updates.length} of your ingredients`}</strong> has
            been updated. Would you like to update your ingredient data and labels? Nothing changes unless you choose
            Update.
          </p>
          {updates.length > 1 && (
            <Checkbox
              style={{ marginBottom: 8 }}
              checked={selected.length === updates.length}
              indeterminate={selected.length > 0 && selected.length < updates.length}
              disabled={processing}
              onChange={(e) => setSelected(e.target.checked ? updates.map((item) => item.ingredientId) : [])}
            >
              Select all
            </Checkbox>
          )}
          {updates.map((item) => (
            <div key={item.ingredientId} className="usda-update-item">
              <div className="usda-update-head">
                <Checkbox
                  className="usda-update-check"
                  checked={selected.includes(item.ingredientId)}
                  disabled={processing}
                  onChange={(e) => toggleSelected(item.ingredientId, e.target.checked)}
                >
                  <span className="usda-update-name">{item.ingredientName}</span>
                </Checkbox>
                <button type="button" className="usda-update-toggle" onClick={() => toggleExpanded(item.ingredientId)}>
                  {expanded.includes(item.ingredientId) ? "Hide changes" : `View changes (${(item.changes || []).length})`}
                </button>
              </div>
              <div className="usda-update-meta">
                {item.updatedAt ? `Updated ${formatDisplayDate(item.updatedAt)}` : ""}
                {item.recipes && item.recipes.length
                  ? `${item.updatedAt ? " · " : ""}Used in ${item.recipes.length} ${item.recipes.length === 1 ? "recipe" : "recipes"}`
                  : ""}
              </div>
              {item.customized && (
                <Alert
                  className="usda-update-warning"
                  type="warning"
                  showIcon
                  message="You edited this ingredient. Updating replaces your changes to the fields below."
                />
              )}
              {expanded.includes(item.ingredientId) && (
                <div className="usda-update-table-wrap">
                  <table className="usda-update-table">
                    <thead>
                      <tr>
                        <th>Field</th>
                        <th>Old value</th>
                        <th>New value</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(item.changes || []).map((change, index) => (
                        <tr key={`${change.field}-${index}`}>
                          <td>{formatFieldLabel(change.field, change.oldValue, change.newValue)}</td>
                          <td>{renderComparedValue(change.oldValue, false)}</td>
                          <td>{renderComparedValue(change.newValue, true)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </Modal>
  );
};

const mapStateToProps = (state) => ({
  userId: state.Auth ? state.Auth.id : null,
  companyId: state.Auth && state.Auth.company ? state.Auth.company.id : null,
});

export default connect(mapStateToProps)(withRouter(UsdaUpdatePrompt));
